import { useCallback, useEffect, useMemo, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { qbBooksReportCsv, qbBooksVarianceUsd, type QbBooksReport } from "@/lib/ops/qbBooksMath";
import { emptyConsumptionReport, type ConsumptionReport } from "@/lib/ops/consumptionReport";
import {
  createOpsQbBooksEvent,
  listOpsQbBooks,
  voidOpsQbBooksEvent,
  type QbBooksEvent,
} from "@/lib/opsQbBooksClient";
import { OpsQbConsumptionSection } from "./OpsQbConsumptionSection";

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function monthStartLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function OpsQbBooksTab() {
  const [periodStart, setPeriodStart] = useState(monthStartLocal);
  const [periodEnd, setPeriodEnd] = useState(todayLocal);
  const [report, setReport] = useState<QbBooksReport | null>(null);
  const [consumption, setConsumption] = useState<ConsumptionReport | null>(null);
  const [events, setEvents] = useState<QbBooksEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [qbAmount, setQbAmount] = useState("");
  const [qbRef, setQbRef] = useState("");
  const [documentLabel, setDocumentLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    const r = await listOpsQbBooks(periodStart, periodEnd);
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      setReport(null);
      setConsumption(null);
      setEvents([]);
      return;
    }
    setMsg("");
    setReport(r.data.report);
    setConsumption(r.data.consumption ?? emptyConsumptionReport(periodStart, periodEnd));
    setEvents(r.data.events);
  }, [periodEnd, periodStart]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const appTotal = report?.total_usd ?? 0;
  const qbParsed = qbAmount.trim() === "" ? appTotal : Number(qbAmount);
  const previewVar = Number.isFinite(qbParsed) ? qbBooksVarianceUsd(appTotal, qbParsed) : 0;

  const csv = useMemo(() => (report ? qbBooksReportCsv(report) : ""), [report]);

  async function copyCsv() {
    if (!csv) return;
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setMsg("Could not copy. Select the table and copy manually.");
    }
  }

  async function onPost() {
    setBusy(true);
    const r = await createOpsQbBooksEvent({
      period_start: periodStart,
      period_end: periodEnd,
      kind: "purchases",
      qb_amount_usd: Number.isFinite(qbParsed) ? qbParsed : appTotal,
      qb_reference: qbRef.trim() || undefined,
      notes: notes.trim() || undefined,
      document_label: documentLabel.trim() || undefined,
    });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setNotes("");
    setDocumentLabel("");
    setQbRef("");
    setQbAmount("");
    await reload();
  }

  async function onVoid(id: string) {
    setBusy(true);
    const r = await voidOpsQbBooksEvent(id);
    setBusy(false);
    if (!r.ok) setMsg(r.error);
    else await reload();
  }

  return (
    <div className="space-y-4">
      <InlineHelp label="How Books works">
        The app keeps material and serial detail (POs, invoices, counts, floor issues). QuickBooks keeps
        period totals. Copy the PO rollup or the consumption report (finished serials + why $ moved), enter
        it in QuickBooks, then record the event and the document that explains why the books moved. Retail
        deal invoices stay under Settings → QuickBooks.
      </InlineHelp>

      {msg ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{msg}</p>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">
          From
          <input
            className="mt-0.5 block rounded border px-2 py-1 text-sm"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </label>
        <label className="text-xs">
          To
          <input
            className="mt-0.5 block rounded border px-2 py-1 text-sm"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </label>
        <button type="button" className="rounded border px-3 py-1.5 text-sm" disabled={busy} onClick={() => void reload()}>
          Refresh
        </button>
        <button type="button" className="rounded border px-3 py-1.5 text-sm" disabled={!csv} onClick={() => void copyCsv()}>
          {copied ? "Copied" : "Copy PO CSV for QuickBooks"}
        </button>
      </div>

      {report ? (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-800 dark:bg-indigo-950/40">
          <div className="text-xs uppercase tracking-wide text-indigo-900 dark:text-indigo-100">
            Issued / received POs in period
          </div>
          <div className="text-2xl font-semibold text-indigo-950 dark:text-indigo-50">{money(report.total_usd)}</div>
          <div className="text-xs text-indigo-900 dark:text-indigo-100">
            {report.po_count} purchase order{report.po_count === 1 ? "" : "s"} · drafts and cancelled POs are excluded
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Vendor</th>
              <th className="px-3 py-2">POs</th>
              <th className="px-3 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(report?.by_vendor ?? []).map((v) => (
              <tr key={v.vendor_name} className="border-t">
                <td className="px-3 py-1.5">{v.vendor_name}</td>
                <td className="px-3 py-1.5">{v.po_count}</td>
                <td className="px-3 py-1.5 text-right font-medium">{money(v.total_usd)}</td>
              </tr>
            ))}
            {!report || report.by_vendor.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-gray-500" colSpan={3}>
                  No committed purchase orders in this period. Issue POs from Manufacturing → Inventory →
                  Generate PO; drafts and cancelled stay out of this total.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <OpsQbConsumptionSection report={consumption} />

      <form
        className="grid gap-2 rounded-xl border border-gray-200 p-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          void onPost();
        }}
      >
        <div className="sm:col-span-2 text-sm font-medium">Record QuickBooks upload</div>
        <label className="text-xs">
          Amount entered in QuickBooks
          <input
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            type="number"
            step="0.01"
            value={qbAmount}
            onChange={(e) => setQbAmount(e.target.value)}
            placeholder={appTotal.toFixed(2)}
          />
        </label>
        <label className="text-xs">
          QuickBooks reference
          <input
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            value={qbRef}
            onChange={(e) => setQbRef(e.target.value)}
            placeholder="Journal / bill batch #"
          />
        </label>
        <label className="text-xs sm:col-span-2">
          Supporting document
          <input
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            value={documentLabel}
            onChange={(e) => setDocumentLabel(e.target.value)}
            placeholder="Filename, Drive link, or packed invoice packet"
          />
        </label>
        <label className="text-xs sm:col-span-2">
          Why this reconciliation happened
          <input
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Monthly materials AP rollup / freight on steel invoices"
          />
        </label>
        <p className="text-sm text-gray-600 sm:col-span-2">
          Variance vs app: <span className="font-semibold">{money(previewVar)}</span>
          {previewVar === 0 ? " (matched)" : ""}
        </p>
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
            Save reconciliation event
          </button>
        </div>
      </form>

      <ul className="max-h-[24rem] divide-y overflow-y-auto rounded-xl border border-gray-200">
        {events.map((ev) => (
          <li key={ev.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="font-medium">
                {ev.period_start} → {ev.period_end} · {ev.status}
              </div>
              <div className="text-xs text-gray-500">
                App {money(ev.app_amount_usd)} · QB {money(ev.qb_amount_usd)} · var {money(ev.variance_usd)}
                {ev.qb_reference ? ` · ${ev.qb_reference}` : ""}
                {ev.document_label ? ` · ${ev.document_label}` : ""}
                {ev.notes ? ` · ${ev.notes}` : ""}
              </div>
            </div>
            {ev.status !== "void" ? (
              <button type="button" className="rounded border px-2 py-0.5 text-xs" onClick={() => void onVoid(ev.id)}>
                Void
              </button>
            ) : null}
          </li>
        ))}
        {events.length === 0 ? (
          <li className="px-3 py-6 text-sm text-gray-500">No QuickBooks reconcile events yet.</li>
        ) : null}
      </ul>
    </div>
  );
}
